import React, { Component, forwardRef, Ref, useImperativeHandle, useLayoutEffect, useRef } from 'react';
import { removeAllChildren } from '../helpers/dom/node';
import { useAttribute } from './useAttribute';
import { useEventListener } from './useEventListener';

export interface ContentEditableProps {
  className?: string;
  onFocus?: (event: FocusEvent) => void;
  onBlur?: (event: FocusEvent) => void;
}

interface ContentEditableElementProps {
  elementRef?: Ref<HTMLDivElement>;
  initialClassName?: string;
}

export const ContentEditable = forwardRef<HTMLDivElement, ContentEditableProps>((props, ref) => {
  const elementRef = useRef<HTMLDivElement | null>(null);

  // Pass on a reference to our element, which we assume stays constant because that component never updates.
  useImperativeHandle(
    ref,
    () => {
      const element = elementRef.current;
      if (!element) {
        throw new Error('Expected elementRef.current to be available in useImperativeHandle initializer');
      }

      return element;
    },
    [],
  );

  // The contenteditable element is retained by Chrome's undo stack, so delete as many references as possible
  // to minimize memory leakage.
  useLayoutEffect(() => {
    // Grab a reference to the element after mount; it shouldn't change until we unmount.
    const element = elementRef.current;

    return () => {
      // Defer until the next macrotask to ensure React is completely done with the element.
      setTimeout(() => {
        if (!element) {
          return;
        }

        deleteReferencesInElement(element);
      });
    };
  }, []);

  // The element never updates, so update attributes and event handlers manually.
  const { className, onFocus, onBlur } = props;
  useAttribute(elementRef, 'class', className);
  useEventListener(elementRef, 'focus', onFocus);
  useEventListener(elementRef, 'blur', onBlur);

  // Pass through className the first time, to avoid styled-components yelling at us.
  return <ContentEditableElement elementRef={elementRef} initialClassName={className} />;
});

// eslint-disable-next-line react/no-multi-comp
class ContentEditableElement extends Component<ContentEditableElementProps> {
  shouldComponentUpdate() {
    return false;
  }

  render() {
    const { elementRef, initialClassName } = this.props;
    return <div ref={elementRef} className={initialClassName} contentEditable />;
  }
}

function deleteReferencesInElement(element: HTMLElement) {
  // Remove the element from its parent node.
  if (element.parentNode) {
    element.parentNode.removeChild(element);
  }

  // Remove all child nodes.
  removeAllChildren(element);

  // Remove the __react* properties.
  const ownPropertyNamesAndSymbols = [
    ...Object.getOwnPropertyNames(element),
    ...Object.getOwnPropertySymbols(element),
  ];
  ownPropertyNamesAndSymbols.forEach(nameOrSymbol => {
    const descriptor = Object.getOwnPropertyDescriptor(element, nameOrSymbol);

    // Check whether this property is configurable, or else the delete will fail.
    if (!descriptor || !descriptor.configurable) {
      return;
    }

    // @ts-ignore
    // eslint-disable-next-line no-param-reassign
    delete element[nameOrSymbol];
  });
}
